import type { Class } from "../types";
import { brand } from "../brand";
import PRODUCTS from "../pages/SignUpPage/products";

function productIdForClassName(className: string): string | undefined {
  const normalized = className.trim().toLowerCase();
  const match = PRODUCTS.find(
    (product) =>
      product.title.toLowerCase() === normalized ||
      (product.legacyClassNames ?? []).some((name) => name.toLowerCase() === normalized),
  );
  return match?.id;
}

/**
 * Stable display order for enrolments / classes (sidebar + dashboard cards).
 * Prefer the brand catalogue order (productIds), then name, then classId.
 */
export function sortEnrolments<T extends Pick<Class, "classId" | "className">>(
  enrolments: readonly T[]
): T[] {
  const productOrder = new Map(
    (brand.productIds as readonly string[]).map((id, index) => [id, index])
  );

  const orderIndex = (className: string): number => {
    const productId = productIdForClassName(className);
    if (productId !== undefined && productOrder.has(productId)) {
      return productOrder.get(productId)!;
    }
    return Number.MAX_SAFE_INTEGER;
  };

  return [...enrolments].sort((a, b) => {
    const orderDiff = orderIndex(a.className) - orderIndex(b.className);
    if (orderDiff !== 0) return orderDiff;

    const nameDiff = a.className.localeCompare(b.className, undefined, {
      sensitivity: "base",
    });
    if (nameDiff !== 0) return nameDiff;

    return a.classId - b.classId;
  });
}
